// frontend/src/services/api.ts
import axios from "axios";
import type {
  Client,
  QueueStats,
  Event,
  ClientStatusType,
} from "../types";

const apiClient = axios.create({
  baseURL: import.meta.env.PROD
    ? import.meta.env.VITE_API_URL
    : "/api",
  headers: {
    "Content-Type": "application/json",
  },
});

const TOKEN_KEY = import.meta.env.VITE_AUTH_TOKEN_KEY || "quelyo_auth_token";
let authToken: string | null = localStorage.getItem(TOKEN_KEY);

export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (token) {
    apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    delete apiClient.defaults.headers.common["Authorization"];
    localStorage.removeItem(TOKEN_KEY);
  }
};

if (authToken) {
  setAuthToken(authToken);
}

// 🛡️ Intercepteur global : auto-déconnexion si le token expire
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/')) {
      // Token expiré ou invalide — nettoyer et rediriger
      localStorage.removeItem(TOKEN_KEY);
      delete apiClient.defaults.headers.common['Authorization'];
      // Ne pas rediriger si déjà sur la page de login
      if (!window.location.pathname.includes('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// ========== AUTHENTIFICATION ==========

export const login = async (email: string, password: string) => {
  const { data } = await apiClient.post("/auth/login", { email, password });
  setAuthToken(data.token);
  return data;
};

export const register = async (registerData: any) => {
  const { data } = await apiClient.post("/auth/register", registerData);
  setAuthToken(data.token);
  return data;
};

export const getCurrentUser = async (token: string) => {
  const { data } = await apiClient.get("/auth/me", {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

// ========== EVENTS ==========

export const verifyEventCode = async (code: string): Promise<Event> => {
  const { data } = await apiClient.post("/events/verify-code", { code });
  return data;
};

export const getEvent = async (eventId: string): Promise<Event> => {
  const { data } = await apiClient.get(`/events/${eventId}`);
  return data;
};

export const getEvents = async (): Promise<Event[]> => {
  const { data } = await apiClient.get(`/events`);
  return data;
};

export const createEvent = async (eventData: any): Promise<Event> => {
  const { data } = await apiClient.post("/events", eventData);
  return data;
};

export const updateEvent = async (eventId: string, eventData: any): Promise<Event> => {
  const { data } = await apiClient.put(`/events/${eventId}`, eventData);
  return data;
};

export const getEventStats = async (eventId: string) => {
  const { data } = await apiClient.get(`/events/${eventId}/stats`);
  return data;
};

// ========== QUEUE ==========

export const joinQueue = async (queueData: any): Promise<any> => {
  const { data } = await apiClient.post("/queue/join", queueData);
  return data;
};

export const getQueue = async (eventId: string): Promise<Client[]> => {
  const { data } = await apiClient.get(`/queue/events/${eventId}`);
  return data;
};

export const getQueueStats = async (eventId: string): Promise<QueueStats> => {
  const { data } = await apiClient.get(`/queue/events/${eventId}/stats`);
  return data;
};

export const getClientPosition = async (clientId: string) => {
  const { data } = await apiClient.get(`/queue/clients/${clientId}/position`);
  return data;
};

export const updateClientStatus = async (clientId: string, status: ClientStatusType): Promise<Client> => {
  const { data } = await apiClient.put(`/queue/clients/${clientId}/status`, { status });
  return data;
};

export const removeClient = async (clientId: string): Promise<void> => {
  await apiClient.delete(`/queue/clients/${clientId}`);
};

export const adjustClientTime = async (clientId: string, adjustment: number) => {
  const { data } = await apiClient.patch(`/queue/clients/${clientId}/adjust-time`, { adjustment });
  return data;
};

// 🆕 Nouveau : Ajuster la durée spécifique
export const adjustClientDuration = async (clientId: string, duration: number) => {
  const { data } = await apiClient.patch(`/v1/queue/client/${clientId}/duration`, { duration });
  return data;
};

// ========== REVIEWS ==========

export const submitReview = async (reviewData: any) => {
  const { data } = await apiClient.post("/reviews", reviewData);
  return data;
};

export const getMemberReviews = async (memberId: string) => {
  const { data } = await apiClient.get(`/reviews/member/${memberId}`);
  return data;
};

// ========== ANALYTICS ==========

export const getAnalytics = async () => {
  const { data } = await apiClient.get("/analytics/dashboard");
  return data;
};

// ========== TEAM ==========

export const getTeam = async () => {
  const { data } = await apiClient.get("/org/team");
  return data;
};

export const inviteUser = async (email: string, role: string) => {
  const { data } = await apiClient.post("/org/invite", { email, role });
  return data;
};

// ========== API KEYS & WEBHOOKS ==========

export const getApiKeys = async () => {
  const { data } = await apiClient.get("/admin/api-keys");
  return data;
};

export const createApiKey = async (name: string) => {
  const { data } = await apiClient.post("/admin/api-keys", { name });
  return data;
};

export const deleteApiKey = async (id: string) => {
  const { data } = await apiClient.delete(`/admin/api-keys/${id}`);
  return data;
};

// ========== WEBHOOKS ==========

export const getWebhooks = async () => {
  const { data } = await apiClient.get("/admin/webhooks");
  return data;
};

export const createWebhook = async (webhookData: { url: string; events: string[] }) => {
  const { data } = await apiClient.post("/admin/webhooks", webhookData);
  return data;
};

export const deleteWebhook = async (id: string) => {
  const { data } = await apiClient.delete(`/admin/webhooks/${id}`);
  return data;
};

export const testWebhook = async (id: string) => {
  const { data } = await apiClient.post(`/admin/webhooks/${id}/test`);
  return data;
};

// ========== SUPER ADMIN ==========

export const getSystemStats = async () => {
  const { data } = await apiClient.get("/admin/system-stats");
  return data;
};

export const getAllOrgs = async () => {
  const { data } = await apiClient.get("/admin/organizations-all");
  return data;
};

export const impersonateOrg = async (orgId: string) => {
  const { data } = await apiClient.post(`/admin/impersonate/${orgId}`);
  return data;
};

// ========== PUSH NOTIFICATIONS ==========

export const getVapidPublicKey = async () => {
  const { data } = await apiClient.get("/push/vapid-key");
  return data.publicKey;
};

export const subscribePush = async (clientId: string, subscription: any) => {
  const { data } = await apiClient.post("/push/subscribe", { clientId, subscription });
  return data;
};

export const getPushStatus = async (clientId: string) => {
  const { data } = await apiClient.get(`/push/status/${clientId}`);
  return data;
};

// ========== CONSENT (WAIVERS) ==========

export const getConsentTemplate = async (orgId: string) => {
  const { data } = await apiClient.get(`/organizations/${orgId}/consent-template`);
  return data;
};

export const signConsent = async (clientId: string, templateId: string, signature: string) => {
  const { data } = await apiClient.post("/gdpr/sign-consent", { clientId, templateId, signature });
  return data;
};

// ========== PORTFOLIO ==========

export const getPortfolio = async (memberId: string) => {
  const { data } = await apiClient.get(`/portfolios/${memberId}`);
  return data;
};

// ========== CSRF ==========

export const getCsrfToken = async () => {
    const { data } = await apiClient.get("/csrf-token");
    return data.csrfToken;
};

export default apiClient;
